import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { toast } from "react-toastify";
import styles from "./LoginPage.module.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const LoginPage = () => {
  const { t } = useTranslation();
  const { login, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") || "";

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef(null);

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .email(t("validation.emailFormat"))
          .required(t("validation.emailRequired")),
        password: yup
          .string()
          .min(4, t("validation.passwordMinLength"))
          .required(t("validation.passwordRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: { email: prefilledEmail },
  });

  const passwordRegister = register("password");

  useEffect(() => {
    if (prefilledEmail && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [prefilledEmail]);

  const onSubmit = async (values) => {
    try {
      setError("");
      await login(values);
      toast.success(t("toasts.loginSuccess"), {
        toastId: "login-success",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("auth.loginFailed");
      setError(msg);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Link to="/">
        <img src="/gredus-logo.svg" alt="logo" className={styles.logo} />
      </Link>

      <div className={styles.card}>
        <h2 className={styles.title}>{t("auth.loginCardTitle")}</h2>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.formGroup}>
            <label className={styles.label}>{t("auth.emailLabel")}</label>
            <input type="email" {...register("email")} className={styles.input} />
            {errors.email && <p className={styles.error}>{errors.email.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{t("auth.passwordLabel")}</label>

            <div className={styles.passwordWrap}>
              <input
                type={showPassword ? "text" : "password"}
                {...passwordRegister}
                ref={(el) => {
                  passwordRegister.ref(el);
                  passwordRef.current = el;
                }}
                className={`${styles.input} ${styles.passwordInput}`}
              />

              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            {errors.password && <p className={styles.error}>{errors.password.message}</p>}
          </div>

          <div className={styles.forgotWrapper}>
            <Link to="/forgot-password" className={styles.forgot}>
              {t("auth.forgotPasswordLink")}
            </Link>
          </div>

          <button type="submit" disabled={isSubmitting || loading} className={styles.submitBtn}>
            {isSubmitting || loading ? t("auth.loginInProgress") : t("auth.loginAction")}
          </button>
        </form>

        <p className={styles.registerText}>
          {t("auth.noAccount")}{" "}
          <Link to="/register" className={styles.registerLink}>
            {t("auth.registerPrompt")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
